import { type ViewModelContentJSON } from './types';
import type {
  AIViewModelContextJSON,
  AIViewModelContextConsistencyLink,
  AIViewModelContextView,
  AIViewModelContextStickyNode,
  AIViewModelContextLink,
  AIViewModelContextZone,
  AIViewModelContextDescriptionPanel as DescriptionPanel,
  ModelId,
  NodeReactionJSON,
  NodeId,
} from 'balus-common';
import {
  AIViewModelContextLinkUtils,
  AIViewModelContextUtils,
  StickyNodeSizeByModelingFormat,
  extractId,
} from 'balus-common';
import { getDefaultStore } from 'jotai';
import { viewModelModelingFormatAtom } from '@user/pages/ViewModelPage/ViewModelModelingFormatAtom';

type CreateContextOptions = {
  // StickyNodeへのリアクション
  stickyNodeReactions?: Record<ModelId, Record<NodeId, NodeReactionJSON>>;
};

// AIに食わせるビューモデルのコンテキストを作成する
export function createViewModelContext(
  viewModel: ViewModelContentJSON,
  options?: CreateContextOptions
): AIViewModelContextJSON {
  // 付箋サイズは ModelingFormat (CJK/LATIN) によって異なるため、現在のビューモデルの設定から解決する
  const { modelingFormat } = getDefaultStore().get(viewModelModelingFormatAtom);
  const stickyNodeSize = StickyNodeSizeByModelingFormat[modelingFormat] ?? StickyNodeSizeByModelingFormat['CJK'];

  const views: AIViewModelContextView[] = viewModel.views.map((view) => {
    const viewId = extractId(view.key);
    const position = viewModel.viewPositions[viewId];

    if (!position) {
      throw new Error(`ViewModelContext: View position not found for view key ${view.key}`);
    }

    // ビューが参照しているモデルを特定し、そのモデルに含まれる要素のみを対象にする
    const targetModel = viewModel.models.find((m) => extractId(m.key) === extractId(view.modelKey));
    if (!targetModel) {
      throw new Error(
        `ViewModelContext: ビュー(id:${JSON.stringify(viewId)}) に対応するモデルが見つかりません: ${view.modelKey}`
      );
    }

    const modelId = extractId(view.modelKey);

    const stickyNodes: AIViewModelContextStickyNode[] = targetModel.nodes.map((node) => {
      const id = extractId(node.key);
      const reactions = (() => {
        const nodeReactions = options?.stickyNodeReactions?.[modelId]?.[id];
        if (!nodeReactions) {
          return undefined;
        }
        return Object.fromEntries(
          Object.entries(nodeReactions).map(([reactionType, userReactions]) => {
            return [reactionType, Object.keys(userReactions).length];
          })
        );
      })();

      return {
        id,
        content: node.name,
        position: node.position,
        size: stickyNodeSize,
        url: node.url,
        description: AIViewModelContextUtils.toElementDescription(targetModel.nodeDescriptions[id]),
        style: {
          strikethrough: node.style.fontStrikethrough,
          themeColor: AIViewModelContextUtils.parseThemeColor(node.style.themeColor) ?? 'White',
        },
        reactions,
      };
    });

    const links: AIViewModelContextLink[] = targetModel.links.map((link) => ({
      id: extractId(link.key),
      from: extractId(link.fromKey),
      to: extractId(link.toKey),
      bend: link.bend,
      label: link.name,
      sourceMultiplicity: link.multiplicity.isEnabled ? link.multiplicity.source : undefined,
      targetMultiplicity: link.multiplicity.isEnabled ? link.multiplicity.target : undefined,
      style: {
        line: AIViewModelContextLinkUtils.parseLineStyle(link.style.lineStyle) ?? 'Solid',
        mark: AIViewModelContextLinkUtils.parseMarkStyle(link.style.markStyle) ?? 'NoMark',
        themeColor: AIViewModelContextLinkUtils.parseThemeColor(link.style.color) ?? 'Gray',
      },
    }));

    const zones: AIViewModelContextZone[] = targetModel.zones.map((zone) => {
      const zPosition = targetModel.zonePositions[extractId(zone.key)];
      if (!zPosition) {
        throw new Error(`ViewModelContext: Zone position not found for zone key ${zone.key}`);
      }

      const id = extractId(zone.key);

      return {
        id,
        name: zone.text,
        position: zPosition,
        size: zone.size,
        description: AIViewModelContextUtils.toElementDescription(targetModel.zoneDescriptions[id]),
        url: zone.url,
        style: {
          themeColor: AIViewModelContextUtils.parseThemeColor(zone.style.themeColor) ?? 'White',
          fontSize: AIViewModelContextUtils.parseZoneFontSize(zone.style.fontSize) ?? 'M',
          textAlign: AIViewModelContextUtils.parseZoneTextAlign(zone.style.textAlign) ?? 'Center',
          shape: AIViewModelContextUtils.parseZoneShape(zone.style.shape) ?? 'Rectangle',
          ...(zone.style.titlePosition
            ? { titlePosition: AIViewModelContextUtils.parseZoneTitlePosition(zone.style.titlePosition) ?? 'Top' }
            : {}),
        },
      };
    });

    const descriptionPanels: DescriptionPanel[] = targetModel.descriptionPanels.panels.map((panel) => {
      const pPosition = targetModel.descriptionPanels.positions[panel.id];

      if (!pPosition) {
        throw new Error(`ViewModelContext: Description panel position not found for panel id ${panel.id}`);
      }

      return {
        id: panel.id,
        content: panel.description,
        position: pPosition,
        size: panel.size,
      };
    });

    return {
      id: extractId(view.key),
      name: view.name,
      position,
      size: view.size,
      nodes: stickyNodes,
      links,
      zones,
      descriptionPanels,
    };
  });

  const viewIds = new Set(views.map((view) => view.id));
  const consistencyLinks: AIViewModelContextConsistencyLink[] = Object.values(viewModel.consistencyLinks)
    .map((link) => ({
      id: extractId(link.key),
      from: extractId(link.fromViewKey),
      to: extractId(link.toViewKey),
    }))
    .filter((link) => viewIds.has(link.from) && viewIds.has(link.to));

  return {
    name: viewModel.name,
    views,
    consistencyLinks,
    modelingFormat,
  };
}
